import {
    createUserWithEmailAndPassword
} from 'firebase/auth';

import {
    arrayUnion,
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    serverTimestamp,
    setDoc,
    updateDoc,
    where
} from 'firebase/firestore';

import { getFunctions, httpsCallable } from 'firebase/functions';

import { app, auth, db } from '../firebase';

// 🔍 Lookup user by UID
export const getUserByUid = async (uid) => {
    const userRef = doc(db, "users", uid);
    const snapshot = await getDoc(userRef);
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
};

// 🆕 Create user profile if missing
export const createUserDocument = async (uid, data) => {
    const ref = doc(db, 'users', uid);
    await setDoc(ref, {
        ...data,
        createdAt: serverTimestamp(),
        signupComplete: true
    }, { merge: true });
    return data;
};

// 💾 Save or update user profile
export const saveUserProfile = async (uid, data) => {
    const ref = doc(db, 'users', uid);
    await setDoc(ref, data, { merge: true });
    console.log(`✅ User profile saved for UID: ${uid}`);
};

// 🔍 Lookup business by code
export const getBusinessByCode = async (code) => {
    const q = query(collection(db, 'businesses'), where('businessCode', '==', code));
    const snapshot = await getDocs(q);
    if (snapshot.docs.length > 1) {
        console.warn(`⚠️ Multiple businesses found with code ${code}`);
    }
    return snapshot.empty ? null : { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
};

// 🔍 Check if business name already exists (case-insensitive)
export const checkBusinessNameExists = async (businessName) => {
    if (!businessName || !businessName.trim()) {
        return false;
    }
    
    const normalizedName = businessName.trim().toLowerCase();
    const businessesRef = collection(db, 'businesses');
    const snapshot = await getDocs(businessesRef);
    
    // Check if any business has the same name (case-insensitive)
    const exists = snapshot.docs.some(doc => {
        const data = doc.data();
        const existingName = data.businessName?.trim().toLowerCase();
        return existingName === normalizedName;
    });
    
    return exists;
};

// 🏢 Save business info for owner
export const saveBusinessInfo = async (uid, data) => {
    // Use Cloud Function to create business securely
    const functions = getFunctions(app);
    const createBusiness = httpsCallable(functions, 'createBusiness');

    try {
        const result = await createBusiness({
            businessData: {
                ...data,
                email: data.email
            }
        });

        console.log(`✅ Business created for UID: ${uid}`);
        // The Cloud Function handles user document creation automatically
        return result.data;
    } catch (error) {
        console.error('Error creating business:', error);
        throw error;
    }
};

// 👥 Connect user to business (staff signup)
export const connectUserToBusiness = async (userUid, businessCode, role = 'staff') => {
    const validRoles = ['staff', 'manager', 'owner'];
    if (!validRoles.includes(role)) {
        throw new Error(`Invalid role: ${role}`);
    }

    const authUser = auth.currentUser;
    if (!authUser || authUser.uid !== userUid) {
        throw new Error('UID mismatch or unauthenticated user');
    }

    const business = await getBusinessByCode(businessCode);
    if (!business) throw new Error('Business not found');

    try {
        // ✅ Step 1: Create user document first
        await createUserDocument(userUid, {
            businessId: business.id,
            businessCode,
            role,
            email: authUser.email,
            joinedAt: serverTimestamp()
        });
        console.log(`✅ User ${userUid} profile created`);

        // ✅ Step 2: Add user to teamUids
        await updateDoc(doc(db, 'businesses', business.id), {
            teamUids: arrayUnion(userUid)
        });
        console.log(`✅ Added ${userUid} to teamUids of business ${business.id}`);
        console.log(`✅ User ${userUid} connected to business ${business.id} as ${role}`);
    } catch (err) {
        console.error(`❌ Error connecting user to business:`, err.message);
        throw new Error('Failed to connect user to business: ' + err.message);
    }
};

// 🧾 Full signup flow for business owner
export const signupOwnerWithBusiness = async (email, password, businessData) => {
    try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const uid = userCredential.user.uid;
        console.log(`✅ Auth user created: ${uid}`);

        await saveBusinessInfo(uid, {
            ...businessData,
            email
        });

        // Business document ID is the same as the UID
        return { success: true, uid, businessId: uid };
    } catch (err) {
        console.error('❌ Signup failed:', err.message);
        return { success: false, error: err.message };
    }
};

// 🖼️ Update user profile image
export const updateUserProfileImage = async (userId, imageUrl) => {
    try {
        const userRef = doc(db, "users", userId);
        await updateDoc(userRef, { profile_image: imageUrl });
        console.log(`✅ Profile image updated for UID: ${userId}`);
    } catch (err) {
        console.error(`❌ Failed to update profile image:`, err.message);
        throw err;
    }
};

// 🔍 Get business by UID
export const getBusinessByUid = async (uid) => {
    const ref = doc(db, 'businesses', uid);
    const snapshot = await getDoc(ref);
    if (!snapshot.exists()) throw new Error(`Business with UID ${uid} not found`);
    return { id: snapshot.id, ...snapshot.data() };
};

// 🔍 Get profile by ID
export const getProfileById = async (id) => {
    const ref = doc(db, 'profiles', id);
    const snapshot = await getDoc(ref);
    if (!snapshot.exists()) throw new Error(`Profile with ID ${id} not found`);
    return { id: snapshot.id, ...snapshot.data() };
};
